import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { useRef, useState, type ChangeEvent, type DragEvent } from 'react'

import { Button, TextArea } from '../../components/ui'
import type { StrainDraft, UploadAsset } from '../../types/submission'
import { imageCount } from '../../types/submission'

interface StrainsStepProps {
  strains: StrainDraft[]
  onChange: (strains: StrainDraft[]) => void
  onFiles: (strainId: string, files: File[]) => void
  onRemoveAsset: (strainId: string, asset: UploadAsset) => void
  onAddStrain: () => void
  onRemoveStrain: (strainId: string) => void
  onSave: () => void
  onReview: () => void
  saving: boolean
}

interface StrainEditorProps {
  strain: StrainDraft
  index: number
  canRemove: boolean
  onUpdate: (strain: StrainDraft) => void
  onFiles: (strainId: string, files: File[]) => void
  onRemoveAsset: (strainId: string, asset: UploadAsset) => void
  onRemove: (strainId: string) => void
}

function StrainEditor({
  strain,
  index,
  canRemove,
  onUpdate,
  onFiles,
  onRemoveAsset,
  onRemove,
}: StrainEditorProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const acceptFiles = (files: FileList | null) => {
    if (!files?.length) return
    onFiles(strain.id, Array.from(files))
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    acceptFiles(event.dataTransfer.files)
  }

  return (
    <section className={`strain-editor ${collapsed ? 'strain-editor--collapsed' : ''}`}>
      <header className="strain-editor__header">
        <button
          type="button"
          className="strain-editor__collapse"
          onClick={() => setCollapsed((value) => !value)}
          aria-expanded={!collapsed}
        >
          <span>Strain {String(index + 1).padStart(2, '0')}</span>
          {collapsed ? <ChevronDown aria-hidden="true" /> : <ChevronUp aria-hidden="true" />}
        </button>
        {canRemove ? (
          <button className="strain-editor__remove" type="button" onClick={() => onRemove(strain.id)}>
            <Trash2 aria-hidden="true" />
            <span>Remove strain</span>
          </button>
        ) : null}
      </header>

      {collapsed ? (
        <div className="strain-editor__summary">
          <strong>{strain.name || 'Untitled strain'}</strong>
          <span>{strain.assets.length} {strain.assets.length === 1 ? 'image' : 'images'}</span>
        </div>
      ) : (
        <div className="strain-editor__body">
          <div className="form-grid form-grid--two">
            <label className="field">
              <span className="field__label">Strain name <span aria-hidden="true">*</span></span>
              <input
                className="field__control"
                value={strain.name}
                onChange={(event) => onUpdate({ ...strain, name: event.target.value })}
                placeholder="e.g., Super Lemon Haze"
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Grower or brand <span className="field__optional">Optional</span></span>
              <input
                className="field__control"
                value={strain.growerOrBrand}
                onChange={(event) => onUpdate({ ...strain, growerOrBrand: event.target.value })}
                placeholder="Your brand or @growername"
              />
            </label>
          </div>

          <div className="field">
            <span className="field__label">Photos <span aria-hidden="true">*</span></span>
            <div
              className={`image-dropzone ${dragging ? 'image-dropzone--dragging' : ''}`}
              onDragEnter={(event) => {
                event.preventDefault()
                setDragging(true)
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              <input
                ref={inputRef}
                className="sr-only"
                type="file"
                aria-label="Choose strain images"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                multiple
                onChange={(event: ChangeEvent<HTMLInputElement>) => acceptFiles(event.target.files)}
              />
              <Upload aria-hidden="true" />
              <p>Drop images here or <button type="button" onClick={() => inputRef.current?.click()}>choose files</button></p>
              <span>JPEG, PNG, WebP or HEIC · up to 25 MB each</span>
            </div>
          </div>

          {strain.assets.length ? (
            <div className="upload-contact-sheet" aria-label={`${strain.name || 'Strain'} images`}>
              {strain.assets.map((asset) => (
                <article className="upload-thumbnail" key={asset.id}>
                  <img src={asset.previewUrl} alt={asset.name} />
                  <button
                    type="button"
                    className="upload-thumbnail__remove"
                    onClick={() => onRemoveAsset(strain.id, asset)}
                    aria-label={`Remove ${asset.name}`}
                  >
                    <X aria-hidden="true" />
                  </button>
                  <div className={`upload-thumbnail__status upload-thumbnail__status--${asset.status}`}>
                    {asset.status === 'ready' ? <Check aria-hidden="true" /> : null}
                    {asset.status === 'error' ? <X aria-hidden="true" /> : null}
                    {asset.status !== 'ready' && asset.status !== 'error' ? (
                      <span className="upload-thumbnail__progress" style={{ '--progress': `${asset.progress}%` } as React.CSSProperties} />
                    ) : null}
                    <span>
                      {asset.status === 'ready'
                        ? 'Upload complete'
                        : asset.status === 'error'
                          ? asset.error ?? 'Upload failed'
                          : asset.status === 'processing'
                            ? 'Processing'
                            : `${asset.progress}%`}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="upload-empty-hint">
              <ImagePlus aria-hidden="true" />
              <span>Your selected images will stay grouped with this strain.</span>
            </div>
          )}

          <TextArea
            label="Notes for the editors"
            value={strain.notes}
            onChange={(event) => onUpdate({ ...strain, notes: event.target.value })}
            placeholder="Share anything helpful about this strain, grow, or photo."
            rows={3}
          />
        </div>
      )}
    </section>
  )
}

export function StrainsStep({
  strains,
  onChange,
  onFiles,
  onRemoveAsset,
  onAddStrain,
  onRemoveStrain,
  onSave,
  onReview,
  saving,
}: StrainsStepProps) {
  const updateStrain = (updated: StrainDraft) => {
    onChange(strains.map((strain) => (strain.id === updated.id ? updated : strain)))
  }
  const totalImages = imageCount({ strains })

  return (
    <section className="step-panel step-panel--photos" aria-labelledby="photos-heading">
      <div className="step-panel__intro">
        <h2 id="photos-heading">Strains & photos</h2>
        <p>Add one strain at a time, then attach its best images.</p>
      </div>

      <div className="strain-list">
        {strains.map((strain, index) => (
          <StrainEditor
            key={strain.id}
            strain={strain}
            index={index}
            canRemove={strains.length > 1}
            onUpdate={updateStrain}
            onFiles={onFiles}
            onRemoveAsset={onRemoveAsset}
            onRemove={onRemoveStrain}
          />
        ))}
      </div>

      <Button className="add-strain-button" variant="outline" onClick={onAddStrain} icon={<Plus size={23} />}>
        Add another strain
      </Button>

      <footer className="step-actions submission-actions">
        <Button variant="quiet" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
        <span className="submission-actions__summary">
          {strains.length} {strains.length === 1 ? 'strain' : 'strains'} · {totalImages} {totalImages === 1 ? 'image' : 'images'}
        </span>
        <Button onClick={onReview} disabled={saving} icon={<ArrowRight size={22} />}>
          Review submission
        </Button>
      </footer>
    </section>
  )
}
